/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { FunctionComponent, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import leftArrow from '../../../../../assets/images/hydra_icon_scrolling_pill_arrow_left.png';
import rightArrow from '../../../../../assets/images/hydra_icon_scrolling_pill_arrow_right.png';
import { DimensionScaler } from '../../../../../core/device/DimensionScaler';
import { RootState, useTivoSelector } from '../../../../../core/tivo/tivo-redux-wrapper';
import { Mousetrap } from '../../../../common';
import { SelectOptionProps } from './SelectOptionProps';

import './SelectOption.scss';

const SelectOption: FunctionComponent<SelectOptionProps> = (props): JSX.Element => {
    const { horizontalPosition, isOverlayGhostHighlightActive, verticalPosition } = useTivoSelector(
        (state: RootState) => state.overlayHighlight,
    );
    const { text, options, isFocused, id, disabled } = props;

    const textRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = buttonRef.current;
        const textContainer = textRef.current;
        if (!textContainer || !container) return;
        const check = (mid) => {
            textContainer.style.fontSize = `${mid}px`;
            return (
                textContainer.scrollWidth <= container.clientWidth - 80 &&
                textContainer.scrollHeight <= container.clientHeight
            );
        };
        let validFonts = DimensionScaler.getInstance().getScaledWidth(24);
        let invalidFonts = DimensionScaler.getInstance().getScaledWidth(37);
        while (validFonts + 1 < invalidFonts) {
            const correctFont = validFonts + Math.floor((invalidFonts - validFonts) / 2);
            if (check(correctFont)) validFonts = correctFont;
            else invalidFonts = correctFont;
        }
        textContainer.style.fontSize = `${validFonts}px`;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [horizontalPosition[id], textRef.current]);

    const createSelectOption = () => {
        return (
            <div
                ref={buttonRef}
                className={`select-button 
    ${isOverlayGhostHighlightActive && id === verticalPosition && !disabled ? 'highlight-button ghost-overlay' : ''} 
    ${isFocused && !disabled ? 'highlight-button' : ''}
    ${text.trim().startsWith('Start from') && !isFocused && !disabled ? 'highlight-blue' : ''}
  `}
            >
                {options.length > 1 ? (
                    <img className="select-option-left-arrow" src={leftArrow}></img>
                ) : (
                    <div className="select-option-left-arrow"></div>
                )}
                <div ref={textRef} className={`select-option-name ${disabled ? 'disabled' : ''}`}>
                    {options[horizontalPosition[id]]}
                </div>
                {options.length > 1 ? (
                    <img className="select-option-right-arrow" src={rightArrow}></img>
                ) : null}
            </div>
        );
    };
    return (
        <div className="select-option-container">
            <div className="select-option-text">{text}</div>
            {disabled ? (
                createSelectOption()
            ) : (
                <Mousetrap value={id}>{createSelectOption()}</Mousetrap>
            )}
        </div>
    );
};

SelectOption.propTypes = {
    options: PropTypes.array.isRequired,
    isFocused: PropTypes.bool.isRequired,
    text: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    id: PropTypes.number.isRequired,
};

export default SelectOption;
